import React from 'react'

import {difference, flatten, groupBy, intersection, pick} from 'es-toolkit'
import {sortBy} from 'es-toolkit/compat'
import invariant from 'invariant'
import {useNavigate} from 'react-router'

import config from '../../config'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {fatalError} from '../../redux/features/core'
import {
  openFinishGroupModal,
  openMaterialModal,
  openProviderInfoSuggestImmediateEditingModal,
} from '../../redux/features/modal'

import useAsync from '../../../../commons/src/hooks/use-async'
import useSearch from '../../../../commons/src/hooks/use-search'
import preventDoubleClick from '../../../../commons/src/services/prevent-double-click'
import useSelectedIds from '../../hooks/use-selected-ids'
import useTableRowFilters, {FilterMap} from '../../hooks/use-table-row-filters'
import getCloudinaryUrl from '../../libs/cloudinary'
import {getMaterialsByAppIds} from '../../libs/material'
import {createInternalMaterialId} from '../../libs/provider-info'
import {
  StringMatchingFilter,
  ToggleMatchingFilter,
  filterTableRows,
} from '../../libs/table-row-filter'
import * as providerInfoApi from '../../services/api/provider-info'
import {captureException} from '../../services/logging'
import {goToEditProviderInfo, goToProviderInfos} from '../../services/navigation'
import {createMaterialSearch} from '../../services/search'

import {MaterialTechnology} from '../../../../commons/src/types'
import {EditProviderInfoTab, MaterialConfigId, MaterialRowContent} from '../../types'

import chevronLeftIcon from '../../../../commons/src/assets/icons/chevron-left.svg'

import ActionBar from '../../../../commons/src/components/action-bar/ActionBar'
import Button from '../../../../commons/src/components/button'
import FilterBar from '../../../../commons/src/components/filter-bar'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import Link from '../../../../commons/src/components/link'
import LinkButton from '../../../../commons/src/components/link-button'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator'
import MaterialImage from '../../../../commons/src/components/material-image'
import Paragraph from '../../../../commons/src/components/paragraph'
import SearchInputField from '../../../../commons/src/components/search-input-field'
import ContentLoader from '../../components/content-loader'
import MaterialActivationLayout from '../../components/material-activation-layout'
import MaterialActivationColumn from '../../components/material-activation-layout/material-activation-column'
import MaterialActivationItem from '../../components/material-activation-layout/material-activation-item'
import PageContentLayout from '../../components/page-content-layout'
import PageHeader from '../../components/page-content-layout/PageHeader'

import StringMatchingFilterPartial from '../filters/string-matching-filter-partial'
import ToggleMatchingFilterPartial from '../filters/toggle-matching-filter-partial'
import PagePartial from '../page-partial'

const initialFilters: FilterMap<MaterialRowContent> = {
  technology: new StringMatchingFilter({
    key: 'technology',
    filterValues: [],
  }),
  materialGroup: new StringMatchingFilter({
    key: 'materialGroupId',
    filterValues: [],
  }),
  printingMethod: new StringMatchingFilter({
    key: 'printingMethodsIds',
    filterValues: [],
  }),
  active: new ToggleMatchingFilter({
    key: 'active',
    filterValue: false,
  }),
}

const CreateProviderInfoPage = () => {
  const dispatch = useAppDispatch()
  const materialConfigs = useAppSelector(state => state.core.materialConfigs)
  const colors = useAppSelector(state => state.core.colors)
  const materials = useAppSelector(state => state.core.materials)
  const materialGroups = useAppSelector(state => state.core.materialGroups)
  const finishGroups = useAppSelector(state => state.core.finishGroups)
  const provider = useAppSelector(state => state.core.provider)
  const printingMethods = useAppSelector(state => state.core.printingMethods)

  // This will never happen when user is authenticated. Just a safety check for TS.
  invariant(provider, 'Provider is missing.')

  const navigate = useNavigate()

  const {isLoading, data: providerInfos = []} = useAsync(
    () => providerInfoApi.getByProvider(provider.id),
    [],
    {
      onError: error => dispatch(fatalError({error})),
    }
  )

  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const {toggleId, toggleAll, isSelected, isAnySelected, selectedIds} =
    useSelectedIds<MaterialConfigId>(() => Object.keys(materialConfigs))

  /* already active materialConfigs which can't be selected and added again */
  const activeMaterialConfigs = React.useMemo(
    () => providerInfos.map(providerInfo => providerInfo.materialConfigId),
    [providerInfos]
  )

  const hasAnyActiveMaterialConfigs = (ids: string[]) =>
    difference(ids, activeMaterialConfigs).length < ids.length

  const materialConfigsByFinishGroup = React.useMemo(() => {
    const sortedMaterialConfigs = sortBy(Object.values(materialConfigs), materialConfig => {
      const color = colors[materialConfig.colorId]
      return color.name
    })
    return groupBy(
      Object.values(sortedMaterialConfigs),
      materialConfig => materialConfig.finishGroupId
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialConfigs])

  const finishGroupsByMaterial = React.useMemo(() => {
    const sortedFinishGroups = sortBy(Object.values(finishGroups), ['name'])
    return groupBy(Object.values(sortedFinishGroups), group => group.materialId)
  }, [finishGroups])

  const materialColumnItems: MaterialRowContent[] = React.useMemo(() => {
    const sortedMaterials = sortBy(Object.values(materials), ['name'])

    const providerAppIds = provider.apps.map(app => app.id)
    const filteredMaterials = getMaterialsByAppIds(sortedMaterials, providerAppIds)

    return Object.values(filteredMaterials).map(material => {
      const finishGroupIdsForMaterial = finishGroupsByMaterial[material.id].map(
        finishGroup => finishGroup.id
      )

      const materialConfigsForMaterial = pick(
        materialConfigsByFinishGroup,
        finishGroupIdsForMaterial
      )

      const materialConfigIds = flatten(Object.values(materialConfigsForMaterial)).map(
        materialConfig => materialConfig.id
      )

      const isAnyActive = hasAnyActiveMaterialConfigs(materialConfigIds)

      return {
        ...material,
        materialGroupName: materialGroups[material.materialGroupId].name,
        printingMethodNames: material.printingMethods.map(printingMethod => printingMethod.name),
        printingMethodsIds: material.printingMethods.map(printingMethod => printingMethod.id),
        active: isAnyActive,
        materialConfigIds,
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materials, providerInfos, provider])

  const materialTechnologyOptions = React.useMemo(
    () =>
      Object.values(MaterialTechnology).map(technology => {
        const label = config.technologyFilterMap[technology].label || technology
        return {
          id: technology,
          label,
          searchable: label,
        }
      }),
    []
  )

  const materialGroupOptions = React.useMemo(() => {
    const sortedMaterialGroups = sortBy(materialGroups, ['name'])
    return sortedMaterialGroups.map(materialGroup => ({
      id: materialGroup.id,
      label: materialGroup.name,
      searchable: materialGroup.name,
    }))
  }, [materialGroups])

  const printingMethodOptions = React.useMemo(() => {
    const sortedPrintingMethods = sortBy(printingMethods, [method => method.name])
    return sortedPrintingMethods.map(printingMethod => ({
      id: printingMethod.id,
      label: printingMethod.name,
      searchable: printingMethod.name,
    }))
  }, [printingMethods])

  const [chosenMaterialId, setChosenMaterialId] = React.useState(
    materialColumnItems.length > 0 ? materialColumnItems[0].id : undefined
  )
  const [chosenFinishGroupId, setChosenFinishGroupId] = React.useState(
    chosenMaterialId ? finishGroupsByMaterial[chosenMaterialId][0].id : undefined
  )

  const [materialConfigIdsForChosenFinishGroup, selectableMaterialConfigIds] = React.useMemo(() => {
    const chosenMaterialConfig = chosenFinishGroupId
      ? materialConfigsByFinishGroup[chosenFinishGroupId]
      : undefined

    // Check if material config is available
    if (chosenMaterialConfig) {
      const materialConfigIdsForFinishGroup = chosenMaterialConfig.map(
        materialConfig => materialConfig.id
      )
      return [
        materialConfigIdsForFinishGroup,
        materialConfigIdsForFinishGroup.filter(id => !activeMaterialConfigs.includes(id)),
      ]
    }
    return [[], []]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenFinishGroupId, activeMaterialConfigs])

  const {filters, setFilter} = useTableRowFilters<MaterialRowContent>(initialFilters)

  const {
    searchResults: materialSearchResults,
    setSearchValue,
    searchValue,
  } = useSearch(materialColumnItems, createMaterialSearch)

  const filteredMaterialColumnItems = React.useMemo(
    () => filterTableRows(materialColumnItems, Object.values(filters)),
    [filters, materialColumnItems]
  )

  const searchedAndFilteredMaterialColumnItems = React.useMemo(
    () => intersection(materialSearchResults, filteredMaterialColumnItems),
    [materialSearchResults, filteredMaterialColumnItems]
  )

  /** Reset chosen material and finishGroup as soon as the filtered items changed. */
  React.useEffect(() => {
    const newChosenMaterialId =
      searchedAndFilteredMaterialColumnItems.length > 0
        ? searchedAndFilteredMaterialColumnItems[0]?.id
        : materialColumnItems[0]?.id
    setChosenMaterialId(newChosenMaterialId)
    setChosenFinishGroupId(finishGroupsByMaterial[newChosenMaterialId]?.[0]?.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedAndFilteredMaterialColumnItems])

  const addSelection = async () => {
    const payload = selectedIds.map(id => {
      const materialConfig = materialConfigs[id]
      const finishGroup = finishGroups[materialConfig.finishGroupId]
      const color = colors[materialConfig.colorId]
      const internalMaterialId = createInternalMaterialId(
        materials[finishGroup.materialId],
        finishGroup,
        color
      )
      const materialPrintingMethods = materials[finishGroup.materialId].printingMethods

      return {
        provider: provider.id,
        available: false,
        materialConfigId: id,
        materialName: null,
        materialId: internalMaterialId,
        finishId: null,
        printingMethodId:
          materialPrintingMethods.length === 1 ? materialPrintingMethods[0].id : null,
        minimumDimensions: null,
        maximumDimensions: null,
        productionTimeFast: null,
        productionTimeSlow: null,
        priceId: null,
        dynamicProductionTimeId: null,
      }
    })

    setIsSubmitting(true)

    try {
      const resultProviderInfos = await providerInfoApi.createMultiple(payload)
      dispatch(
        openProviderInfoSuggestImmediateEditingModal({
          count: resultProviderInfos.length,
          onConfirm: () => {
            goToEditProviderInfo(navigate, {
              providerInfos: resultProviderInfos,
              activeTab: EditProviderInfoTab.DIMENSIONS,
            })
          },
          onCancel: () => goToProviderInfos(navigate),
        })
      )
    } catch (error) {
      setIsSubmitting(false)
      dispatch(fatalError({error: error as Error}))
    }
  }

  const renderMaterialItems = () =>
    searchedAndFilteredMaterialColumnItems.map((material, index) => {
      const hasSelectedConfigs = isAnySelected(material.materialConfigIds)

      const counter = intersection(selectedIds, material.materialConfigIds).length || undefined

      const materialPrintingMethodsCount = material.printingMethods
        .map(printingMethod => printingMethod.name)
        .join(', ')

      return (
        <MaterialActivationItem
          key={index}
          title={material.name}
          subtitle={materialPrintingMethodsCount}
          checked={hasSelectedConfigs}
          active={material.active}
          counter={counter}
          image={
            <MaterialImage
              size="s"
              square
              image={getCloudinaryUrl(material.featuredImage, ['w_700', 'h_458', 'c_fill'])}
            />
          }
          selected={material.id === chosenMaterialId}
          onClick={() => {
            setChosenMaterialId(material.id)
            setChosenFinishGroupId(finishGroupsByMaterial[material.id][0].id)
          }}
          link={
            <Link onClick={() => dispatch(openMaterialModal({materialId: material.id}))}>
              Learn more
            </Link>
          }
        />
      )
    })

  const renderFinishGroupItems = () => {
    if (!chosenMaterialId) return null
    return finishGroupsByMaterial[chosenMaterialId].map((finishGroup, index) => {
      const selectedMaterialConfigs = materialConfigsByFinishGroup[finishGroup.id] || [] // It's possible that no materialConfig is in the catalogue

      const materialConfigIds = selectedMaterialConfigs.map(materialConfig => materialConfig.id)

      const hasActiveConfigs = hasAnyActiveMaterialConfigs(materialConfigIds)
      const hasSelectedConfigs = isAnySelected(materialConfigIds)
      const counter = intersection(selectedIds, materialConfigIds).length || undefined

      const colorsCount = `${materialConfigIds.length} color${
        materialConfigIds.length > 1 ? 's' : ''
      }`

      return (
        <MaterialActivationItem
          key={index}
          title={finishGroup.name}
          subtitle={colorsCount}
          checked={hasSelectedConfigs}
          active={hasActiveConfigs}
          counter={counter}
          image={
            <MaterialImage
              size="s"
              square
              image={getCloudinaryUrl(finishGroup.featuredImage, ['w_700', 'h_458', 'c_fill'])}
            />
          }
          selected={finishGroup.id === chosenFinishGroupId}
          onClick={() => setChosenFinishGroupId(finishGroup.id)}
          link={
            <Link onClick={() => dispatch(openFinishGroupModal({finishGroupId: finishGroup.id}))}>
              Learn more
            </Link>
          }
        />
      )
    })
  }

  const renderColorItems = () => {
    if (!chosenMaterialId || !chosenFinishGroupId) return null
    const selectedMaterialConfigs = materialConfigsByFinishGroup[chosenFinishGroupId]

    if (!selectedMaterialConfigs || selectedMaterialConfigs.length === 0) {
      captureException(new Error('Material is missing MaterialConfig'), 'default', {
        material: materials[chosenMaterialId],
        finish: finishGroups[chosenFinishGroupId],
      })
      return (
        <Paragraph minor size="s" className="u-margin-left">
          No colors available
        </Paragraph>
      )
    }

    return selectedMaterialConfigs.map((materialConfig, index) => {
      const color = colors[materialConfig.colorId]
      const materialConfigId = materialConfig.id

      // This will never happen. Just a safety check for TS.
      invariant(materialConfigId, 'No matching materialConfig Id was found')

      const activeMaterialConfigsCount = activeMaterialConfigs.filter(
        configId => configId === materialConfigId
      ).length
      const isActive = activeMaterialConfigsCount > 0
      const isSelectedToAdd = isSelected(materialConfigId)

      return (
        <MaterialActivationItem
          key={index}
          title={color.name}
          checked={isSelectedToAdd}
          active={isActive}
          activeLabel={isActive ? `${activeMaterialConfigsCount} existing` : ''}
          image={
            <MaterialImage
              size="s"
              square
              border
              image={color.image && getCloudinaryUrl(color.image, ['w_700', 'h_458', 'c_fill'])}
              color={color.hexCode}
            />
          }
          action={
            isSelectedToAdd ? (
              <LinkButton appearance="minor" onClick={() => toggleId(materialConfigId)}>
                Deselect
              </LinkButton>
            ) : (
              <LinkButton onClick={() => toggleId(materialConfigId)}>Select</LinkButton>
            )
          }
        />
      )
    })
  }

  return (
    <PagePartial hideFooter fixedHeight>
      <PageContentLayout
        fixedHeight
        header={
          <PageHeader
            headline={
              <ActionBar>
                <Button
                  hierarchy="secondary"
                  icon={<Icon source={chevronLeftIcon} />}
                  onClick={() => goToProviderInfos(navigate)}
                />
                <Headline size="l" label="Add Materials" className="u-no-margin-bottom" />
              </ActionBar>
            }
            actions={
              <Button
                onClick={preventDoubleClick(() => addSelection())}
                icon={isSubmitting ? <LoadingIndicator invert /> : undefined}
                disabled={isLoading || isSubmitting || selectedIds.length === 0}
              >
                {selectedIds.length > 0 ? `Add selection (${selectedIds.length})` : 'Add selection'}
              </Button>
            }
          />
        }
        loader={<ContentLoader loading={isLoading} />}
      >
        {!isLoading && (
          <MaterialActivationLayout
            isEmpty={searchedAndFilteredMaterialColumnItems.length === 0}
            empty={
              <Paragraph className="u-align-center" minor>
                {materialColumnItems.length > 0 &&
                searchedAndFilteredMaterialColumnItems.length === 0
                  ? 'No materials were found for the set filters.'
                  : 'No materials were found.'}
              </Paragraph>
            }
            filterBar={
              <FilterBar
                filtersLabel="Filters:"
                filters={
                  <>
                    <StringMatchingFilterPartial
                      label="Technology"
                      size="90"
                      options={materialTechnologyOptions}
                      filterKey="technology"
                      filterMap={filters}
                      tableRows={materialSearchResults}
                      applyFilter={newFilter => setFilter('technology', newFilter)}
                    />
                    <StringMatchingFilterPartial
                      label="Material category"
                      size="90"
                      options={materialGroupOptions}
                      filterKey="materialGroup"
                      filterMap={filters}
                      tableRows={materialSearchResults}
                      applyFilter={newFilter => setFilter('materialGroup', newFilter)}
                      showSearch
                    />
                    <StringMatchingFilterPartial
                      size="90"
                      label="Manufacturing process"
                      options={printingMethodOptions}
                      filterKey="printingMethod"
                      filterMap={filters}
                      tableRows={materialSearchResults}
                      applyFilter={newFilter => setFilter('printingMethod', newFilter)}
                      showSearch
                    />
                    <ToggleMatchingFilterPartial
                      label="only offered materials"
                      filterKey="active"
                      filterMap={filters}
                      tableRows={materialSearchResults}
                      applyFilter={newFilter => setFilter('active', newFilter)}
                    />
                  </>
                }
                searchBar={
                  <SearchInputField
                    size="90"
                    name="shippings-search"
                    placeholder="Search by material name"
                    value={searchValue}
                    onChange={value => {
                      setSearchValue(value)
                    }}
                  />
                }
              />
            }
          >
            <MaterialActivationColumn title="Materials">
              {renderMaterialItems()}
            </MaterialActivationColumn>
            <MaterialActivationColumn title="Finishes">
              {renderFinishGroupItems()}
            </MaterialActivationColumn>
            <MaterialActivationColumn
              title="Colors"
              action={
                isAnySelected(materialConfigIdsForChosenFinishGroup) ? (
                  <LinkButton onClick={() => toggleAll(materialConfigIdsForChosenFinishGroup)}>
                    Deselect all
                  </LinkButton>
                ) : (
                  <LinkButton
                    onClick={() => toggleAll(selectableMaterialConfigIds)}
                    disabled={selectableMaterialConfigIds.length === 0}
                  >
                    Select all
                  </LinkButton>
                )
              }
            >
              {renderColorItems()}
            </MaterialActivationColumn>
          </MaterialActivationLayout>
        )}
      </PageContentLayout>
    </PagePartial>
  )
}

export default CreateProviderInfoPage
